import { apiBaseUrl, assetBaseUrl } from "../config/env";
import axios from "../api/axios";

const resolvedAssetBase = (assetBaseUrl || apiBaseUrl || "").replace(/\/$/, "");
const IMAGE_MIME_PREFIX = "image/";
const DEFAULT_ALLOWED_EXTENSIONS = [".png", ".jpg", ".jpeg", ".webp", ".svg", ".gif", ".avif"] as const;
const DEFAULT_MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

export function resolveAssetUrl(raw?: string | null): string | undefined {
  if (!raw) {
    return undefined;
  }
  if (/^https?:\/\//i.test(raw)) {
    return raw;
  }
  const trimmed = raw.replace(/^\/+/, "");
  const stripped = trimmed.replace(/^(?:uploads\/)+/i, "");
  const relative = stripped ? `uploads/${stripped}` : "uploads";
  if (resolvedAssetBase) {
    return `${resolvedAssetBase.replace(/\/api$/, "")}/${relative}`;
  }
  return `/${relative}`;
}

type UploadAssetOptions = {
  maxFileSizeBytes?: number;
  allowedExtensions?: readonly string[];
};

function extractFileExtension(fileName: string): string {
  const index = fileName.lastIndexOf(".");
  if (index < 0) {
    return "";
  }
  return fileName.slice(index).toLowerCase();
}

function normalizeExtensionList(extensions?: readonly string[]): string[] {
  if (!extensions || extensions.length === 0) {
    return [...DEFAULT_ALLOWED_EXTENSIONS];
  }
  return extensions
    .map((value) => value.trim().toLowerCase())
    .filter((value) => value.length > 0)
    .map((value) => (value.startsWith(".") ? value : `.${value}`));
}

function getApiErrorMessage(error: unknown): string | null {
  const message =
    (error as any)?.response?.data?.message ??
    (error as any)?.response?.data?.error ??
    (error as any)?.message;
  if (typeof message !== "string") {
    return null;
  }
  const trimmed = message.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export async function uploadAdminAsset(endpoint: string, file: File, options?: UploadAssetOptions): Promise<string | null> {
  const maxFileSizeBytes = options?.maxFileSizeBytes ?? DEFAULT_MAX_FILE_SIZE_BYTES;
  const allowedExtensions = normalizeExtensionList(options?.allowedExtensions);
  const extension = extractFileExtension(file.name);

  if (!file || file.size <= 0) {
    throw new Error("Gecerli bir dosya secin.");
  }
  if (file.size > maxFileSizeBytes) {
    const maxMb = (maxFileSizeBytes / (1024 * 1024)).toFixed(0);
    throw new Error(`Dosya boyutu cok buyuk. En fazla ${maxMb} MB yukleyebilirsiniz.`);
  }
  if (!file.type?.startsWith(IMAGE_MIME_PREFIX)) {
    throw new Error("Sadece gorsel dosyalari yukleyebilirsiniz.");
  }
  if (extension && !allowedExtensions.includes(extension)) {
    throw new Error(`Desteklenmeyen dosya uzantisi: ${extension}.`);
  }

  try {
    const formData = new FormData();
    formData.append("file", file);
    const res = await axios.post(endpoint, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    const fileName = res.data?.fileName ?? res.data;
    return typeof fileName === "string" && fileName.trim().length > 0 ? fileName.trim() : null;
  } catch (error) {
    console.error("[admin] asset upload failed", endpoint, error);
    const message = getApiErrorMessage(error);
    throw new Error(message ?? "Dosya yuklenemedi. Lutfen tekrar deneyin.");
  }
}
